import { CalendarDays, CalendarClock, ChevronRight, CheckCheck } from 'lucide-react';
import { avatarGradient, initials } from './ShiftCardHeader';

const fmtCas = t => (t || '').toString().slice(0, 5);

const DNY = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];

function popisDne(datum) {
    if (!datum) return '';
    const d = new Date(typeof datum === 'string' ? datum.slice(0, 10) : datum);
    if (isNaN(d.getTime())) return '';
    const dnes = new Date();
    const rozdil = Math.round((d.setHours(0, 0, 0, 0) - new Date(dnes).setHours(0, 0, 0, 0)) / 86_400_000);
    const zaklad = `${DNY[new Date(d).getDay()]} ${new Date(d).getDate()}. ${new Date(d).getMonth() + 1}.`;
    if (rozdil === 1) return `zítra · ${zaklad}`;
    if (rozdil > 1 && rozdil < 7) return `za ${rozdil} dny · ${zaklad}`;
    return zaklad;
}

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

const fmtHodiny = (h) => {
    if (h == null || isNaN(h)) return '—';
    const t = Math.round(Number(h) * 60);
    return `${Math.floor(t / 60)} h ${String(t % 60).padStart(2, '0')} min`;
};

function SouhrnRadek({ popis, hodnota, tlumene, barva }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 13, color: tlumene }}>{popis}</span>
            <strong style={{
                fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                color: barva || 'var(--text-primary)',
            }}>{hodnota}</strong>
        </div>
    );
}

function dnesniDatum() {
    const d = new Date();
    return `${DNY[d.getDay()][0].toUpperCase()}${DNY[d.getDay()].slice(1)} ${d.getDate()}. ${d.getMonth() + 1}.`;
}

/**
 * Obrazovka volna.
 *
 * Když dnes směna není, backend sáhne pro **nejbližší budoucí** — a modul ji
 * dosud vykreslil jako obyčejnou kartu s tlačítkem „Začít v 10:00“. Obrazovka
 * se jmenuje „Dnešní směna“, nabízí ji začít (tlačítko je sice neaktivní, ale
 * nápis zůstává) a že dnes nic není, neřekne nikde. Jediná stopa je malé datum
 * pod názvem a odpočet „start za 3d 18h“.
 *
 * Tady se to obrací: nejdřív se řekne, že je volno, a příští směna je pod tím
 * jako **řádek** — informace, ne výzva k akci. Karta s tlačítkem se objeví až
 * v den směny.
 *
 * @param {Object|null} pristiSmena - nejbližší budoucí směna, nebo null
 * @param {Array} tym - kolegové na té směně
 * @param {() => void} [onOtevritKalendar]
 */
export default function Volno({ pristiSmena = null, tym = [], poSmene = false, souhrn = null, onOtevritKalendar, isDark }) {
    const tlumene = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';

    return (
        <div style={{
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            gap: 14, padding: '0 16px',
        }}>
            <div style={{ textAlign: 'center' }}>
                <span style={{
                    width: 52, height: 52, borderRadius: 14, margin: '0 auto 12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(12,192,223,0.12)', color: '#0CC0DF',
                }}>
                    {poSmene ? <CheckCheck size={25} /> : <CalendarDays size={25} />}
                </span>
                <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.2, color: 'var(--text-primary)' }}>
                    {poSmene ? 'Dnes máš odpracováno' : 'Dnes máš volno'}
                </div>
                <div style={{ fontSize: 13, marginTop: 3, color: tlumene }}>
                    {dnesniDatum()}
                </div>
            </div>

            {poSmene && souhrn && (souhrn.hodiny_odpracovane != null || souhrn.mzda_kc != null) && (
                // Co sis dnes vydělal. Stálo to na samostatné obrazovce, která
                // se po šesti sekundách sama zavřela — informace, kterou si
                // nestihneš přečíst, je k ničemu. Tady zůstane.
                <div style={{
                    borderRadius: 12,
                    border: '1px solid rgba(16,185,129,0.30)',
                    background: isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.07)',
                    padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                    {souhrn.hodiny_odpracovane != null && (
                        <SouhrnRadek popis="Odpracováno"
                            hodnota={fmtHodiny(souhrn.hodiny_odpracovane)} tlumene={tlumene} />
                    )}
                    {souhrn.mzda_kc != null && (
                        <SouhrnRadek popis="Mzda za dnešek"
                            hodnota={fmtKc(souhrn.mzda_kc)} tlumene={tlumene} />
                    )}
                    {souhrn.dysko_kc > 0 && (
                        <SouhrnRadek popis="Dýško" hodnota={fmtKc(souhrn.dysko_kc)}
                            tlumene={tlumene} barva="#10B981" />
                    )}
                </div>
            )}

            {pristiSmena && (
                // Příští směna jako řádek, ne karta s tlačítkem. Kdo má volno,
                // potřebuje vědět kdy zas — ne dostat nabídku ji začít.
                <div style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'var(--border-default)'}`,
                    background: isDark ? 'rgba(255,255,255,0.035)' : 'var(--surface-elevated)',
                    padding: '11px 13px',
                    display: 'flex', alignItems: 'center', gap: 11,
                }}>
                    <span style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,138,0,0.13)', color: '#FF8A00',
                    }}>
                        <CalendarClock size={16} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                            textTransform: 'uppercase', color: tlumene,
                        }}>Příští směna</div>
                        <div style={{
                            fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginTop: 1,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {pristiSmena.sablon_nazev || 'Směna'}
                        </div>
                        <div style={{ fontSize: 11.5, color: tlumene, marginTop: 1 }}>
                            {popisDne(pristiSmena.datum)} · {fmtCas(pristiSmena.cas_od)}–{fmtCas(pristiSmena.cas_do)}
                        </div>
                    </div>
                    {tym.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                            {tym.slice(0, 3).map((m, i) => (
                                <span key={m.zamestnanec_id ?? i} style={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    marginLeft: i === 0 ? 0 : -7,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 8, fontWeight: 800, color: '#fff',
                                    background: avatarGradient(m.zamestnanec_id),
                                    border: `1.5px solid ${isDark ? '#141418' : '#fff'}`,
                                    overflow: 'hidden',
                                }}>
                                    {m.avatar_url
                                        ? <img src={m.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : initials(m.jmeno, m.prijmeni)}
                                </span>
                            ))}
                        </span>
                    )}
                </div>
            )}

            {onOtevritKalendar && (
                <button type="button" onClick={onOtevritKalendar} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    minHeight: 44, borderRadius: 11, cursor: 'pointer', font: 'inherit',
                    fontSize: 14, fontWeight: 700, color: '#0CC0DF',
                    background: 'rgba(12,192,223,0.12)', border: '1px solid rgba(12,192,223,0.34)',
                    WebkitTapHighlightColor: 'transparent',
                }}>
                    Otevřít Můj měsíc
                    <ChevronRight size={16} />
                </button>
            )}
        </div>
    );
}
